import { AxiosError, AxiosResponse } from 'axios';
import instance, { ApiError } from '../../api/instance';
import {
  CompaniesPayload,
  Company,
  CompanyDetails,
  DeviceScreen,
  DraftCompany,
  DraftCompanyImages,
} from './companies.types';
import { CompanyAccount } from '../companyAccounts/companyAccounts.types';
import { UploadFileToBucket } from 'src/api/utils/uploadFileToBucket';

const companiesApiPath = '/companies';

export const getCompanies = async ({
  name,
}: {
  name?: string;
}): Promise<AxiosResponse<CompaniesPayload & ApiError>> => {
  return instance
    .get<CompaniesPayload>(companiesApiPath, { params: { name } })
    .catch(e => e.response as AxiosError<ApiError> as never);
};

export const createCompany = async (data: {
  name: string;
  location: string;
}): Promise<AxiosResponse<Company & ApiError>> => {
  return instance.post<Company>(companiesApiPath, data).catch(e => e.response as AxiosError<ApiError> as never);
};

const companyApiPath = (companyId: string) => `/companies/${companyId}`;

export const getCompany = async ({ companyId }: { companyId: string }): Promise<AxiosResponse<Company & ApiError>> => {
  return instance.get<Company>(companyApiPath(companyId)).catch(e => e.response as AxiosError<ApiError> as never);
};

export const updateCompany = async ({
  companyId,
  data,
}: {
  data: Partial<DraftCompany & { deviceScreens: DeviceScreen[] }>;
  companyId: string;
}): Promise<AxiosResponse<Company & ApiError>> => {
  return instance
    .post<Company>(companyApiPath(companyId), data)
    .catch(e => e.response as AxiosError<ApiError> as never);
};

export const updateCompanyimages = async ({
  companyId,
  data,
}: {
  companyId: string;
  data: Partial<DraftCompanyImages>;
}): Promise<AxiosResponse<Company & ApiError>> => {
  return instance
    .post<Company>(companyApiPath(companyId), data)
    .catch(e => e.response as AxiosError<ApiError> as never);
};

export const deleteCompany = async ({
  companyId,
}: {
  companyId: string;
}): Promise<AxiosResponse<Company & ApiError>> => {
  return instance.delete<Company>(companyApiPath(companyId)).catch(e => e.response as AxiosError<ApiError> as never);
};

const companyAccountsApiPath = (companyId: string) => `/companies/${companyId}/accounts`;

export const getCompanyAccounts = async ({
  companyId,
}: {
  companyId: string;
}): Promise<AxiosResponse<CompanyAccount[] & ApiError>> => {
  return instance
    .get<CompanyAccount[]>(companyAccountsApiPath(companyId))
    .catch(e => e.response as AxiosError<ApiError> as never);
};

export const bindAccountToCompany = async ({
  companyId,
  body,
}: {
  companyId: string;
  body: { ids: string[] };
}): Promise<AxiosResponse<CompanyAccount[] & ApiError>> => {
  return instance
    .post<CompanyAccount[]>(companyAccountsApiPath(companyId), body)
    .catch(e => e.response as AxiosError<ApiError> as never);
};

export const unbindAccountToCompany = async ({
  companyId,
  accountId,
}: {
  companyId: string;
  accountId: string;
}): Promise<AxiosResponse<void & ApiError>> => {
  return instance
    .delete(`${companyAccountsApiPath(companyId)}/${accountId}`)
    .catch(e => e.response as AxiosError<ApiError> as never);
};

export type GetHeadsetActivationCode = (params: {
  companyId: string;
}) => Promise<AxiosResponse<{ code: string; expireAt: string }>>;

export const getHeadsetActivationCode: GetHeadsetActivationCode = async ({ companyId }) => {
  return instance.get(`companies/${companyId}/activation_code`).catch(e => e.response as never);
};

export type PostCompanyActivate = (params: { companyId: string }) => Promise<AxiosResponse<Company>>;

export const postCompanyActivate: PostCompanyActivate = async ({ companyId }) => {
  return instance.post(`companies/${companyId}/activate`).catch(e => e.response as never);
};

const companyDetailsApiPath = (companyId: string) => `/companies/${companyId}/details`;

export const addCompanyDetails = async ({
  companyId,
  details,
}: {
  companyId: string;
  details: CompanyDetails;
}): Promise<AxiosResponse<CompanyDetails & ApiError>> => {
  return instance
    .post<CompanyDetails>(companyDetailsApiPath(companyId), details)
    .catch(e => e.response as AxiosError<ApiError> as never);
};

const companyDetailsRecordApiPath = (companyId: string, recordId: string) =>
  `/companies/${companyId}/details/${recordId}`;

export const updateCompanyDetails = async ({
  companyId,
  recordId,
  details,
}: {
  companyId: string;
  recordId: string;
  details: Partial<CompanyDetails>;
}): Promise<AxiosResponse<CompanyDetails & ApiError>> => {
  return instance
    .post<CompanyDetails>(companyDetailsRecordApiPath(companyId, recordId), details)
    .catch(e => e.response as AxiosError<ApiError> as never);
};

export const deleteCompanyDetails = async ({
  companyId,
  recordId,
}: {
  companyId: string;
  recordId: string;
}): Promise<AxiosResponse<void & ApiError>> => {
  return instance
    .delete(companyDetailsRecordApiPath(companyId, recordId))
    .catch(e => e.response as AxiosError<ApiError> as never);
};

export type UploadCompanyImage = (params: {
  companyId: string;
  data: { filename: string };
}) => Promise<AxiosResponse<Parameters<UploadFileToBucket>[0]>>;

const companyImageApiPath = (companyId: string) => `/companies/${companyId}/images`;

export const uploadCompanyImage: UploadCompanyImage = async ({ companyId, data }) => {
  return instance.post(companyImageApiPath(companyId), data).catch(e => e.response as never);
};
